import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download, Trash2, Eye, File, Image, Sheet, Search } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

function getFileIcon(contentType) {
  if (!contentType) return FileText;
  if (contentType.includes('image')) return Image;
  if (contentType.includes('sheet') || contentType.includes('excel') || contentType.includes('csv')) return Sheet;
  return File;
}

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/documents/clients-with-docs')
      .then((r) => setClients(r.data || []))
      .catch(() => {});
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedClient === 'all') {
        const allDocs = [];
        for (const c of clients) {
          const { data } = await api.get(`/documents/client/${c.id}`);
          const docsWithClient = data.map((d) => ({ ...d, client_name: c.trust_name, client_trust_id: c.trust_id }));
          allDocs.push(...docsWithClient);
        }
        setDocuments(allDocs.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')));
      } else {
        const client = clients.find((c) => c.id === selectedClient);
        const { data } = await api.get(`/documents/client/${selectedClient}`);
        const docsWithClient = data.map((d) => ({ ...d, client_name: client?.trust_name || '', client_trust_id: client?.trust_id || '' }));
        setDocuments(docsWithClient);
      }
    } catch {
      toast.error('Failed to load documents');
    }
    setLoading(false);
  }, [selectedClient, clients]);

  useEffect(() => {
    if (clients.length > 0) {
      fetchDocuments();
    } else {
      setLoading(false);
    }
  }, [clients, selectedClient, fetchDocuments]);

  const filteredDocs = documents.filter((d) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.original_filename?.toLowerCase().includes(q) ||
      d.client_name?.toLowerCase().includes(q) ||
      d.client_trust_id?.toLowerCase().includes(q)
    );
  });

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === filteredDocs.length) {
      setSelected([]);
    } else {
      setSelected(filteredDocs.map((d) => d.id));
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleExportSelected = async () => {
    const docsToExport = selected.length > 0
      ? filteredDocs.filter((d) => selected.includes(d.id))
      : filteredDocs;

    if (!docsToExport.length) return;
    setExporting(true);
    toast.info(`Downloading ${docsToExport.length} file(s)...`);

    for (const doc of docsToExport) {
      try {
        const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        a.click();
        URL.revokeObjectURL(url);
        await new Promise((r) => setTimeout(r, 300));
      } catch {
        toast.error(`Failed to download: ${doc.original_filename}`);
      }
    }
    setExporting(false);
    toast.success('Download complete');
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      setSelected((prev) => prev.filter((id) => id !== docId));
      fetchDocuments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} document(s)?`)) return;
    try {
      for (const id of selected) {
        await api.delete(`/documents/${id}`);
      }
      toast.success(`Deleted ${selected.length} document(s)`);
      setSelected([]);
      fetchDocuments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('documents')}
        </h1>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-docs-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete ({selected.length})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportSelected}
            disabled={exporting || filteredDocs.length === 0}
            data-testid="export-docs-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {exporting ? 'Downloading...' : selected.length > 0 ? `Export (${selected.length})` : 'Export All'}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-4 mb-5">
            <div className="space-y-1 w-64">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Select Client
              </label>
              <Select value={selectedClient} onValueChange={(v) => { setSelectedClient(v); setSelected([]); }}>
                <SelectTrigger data-testid="doc-client-filter">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="all">All Clients ({clients.length})</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.trust_id} - {c.trust_name || 'Unnamed'} ({c.doc_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="doc-search"
              />
            </div>
            {selectedClient !== 'all' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/clients/${selectedClient}`)}
                data-testid="view-client-btn"
              >
                <Eye className="h-3.5 w-3.5 mr-1.5" /> View Client
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No documents match your search' : (selectedClient === 'all' ? 'No documents uploaded yet' : 'No documents for this client')}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filteredDocs.length > 0 && selected.length === filteredDocs.length}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-docs"
                      />
                    </TableHead>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead className="hidden md:table-cell">Client</TableHead>
                    <TableHead className="hidden lg:table-cell">Size</TableHead>
                    <TableHead className="hidden lg:table-cell">Uploaded By</TableHead>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                    <TableHead className="w-24">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocs.map((doc) => {
                    const IconComp = getFileIcon(doc.content_type);
                    return (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selected.includes(doc.id)}
                            onCheckedChange={() => toggleSelect(doc.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconComp className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                        <TableCell className="font-medium text-sm max-w-[200px] truncate">
                          {doc.original_filename}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] font-mono">{doc.client_trust_id}</Badge>
                            <span className="text-sm truncate max-w-[120px]">{doc.client_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs tabular-nums text-muted-foreground">
                          {formatSize(doc.size)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {doc.uploaded_by_name || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground tabular-nums">
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {filteredDocs.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
              {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
              {searchQuery && ` (filtered from ${documents.length})`}
              {selected.length > 0 && ` · ${selected.length} selected`}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
